import React, { Suspense, lazy, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { message, notification, Spin } from "antd";
import { useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/UI/toaster.jsx';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'antd/dist/antd.css';
import '../src/assets/style.css';

import '../src/assets/images/no-image.png'

import axios, { awsAxios } from '../axios_config';
import config from '../src/config/config';

import { AUTH_ROUTES } from './routes/AuthRoute';
import { ONBOARDING_ROUTES } from './routes/PostSignUpRoute';
import { PRIVATE_APP_ROUTES } from './routes/AppRouteContainer';
import { MOBILE_ROUTES } from './constants/routes.js'

import { getUserCurrentSubscription, GetCurrentUser } from './slices/auth.js'
import handleRouteValidations from './utils/routeValidations';

import { STORAGE_KEY } from './constants/onBoarding';

import ProtectedRoute from './components/ProtectedRoute';

import 'ag-grid-enterprise';

const Layout = lazy(() => import("./components/layout/layout.jsx"));
const MobileLayout = lazy(() => import("./components/layout/mobile-layout.jsx"));
const { theme } = config;

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isUserFetched, setIsUserFetched] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const {
    token,
    currentUser: user,
    fetchingCurrentUser,
    impersonatingUser,
    signOutLoading,
  } = useSelector((state) => state.auth || {});

  const { currentActiveSubscription, fetchingCurrentActiveSubscription, signInLoading, fetchedSubscription } = useSelector((state) => state.auth) || {};

  const { loading: warehouseLoading } = useSelector(
    (state) => state.warehouse || {}
  );
  const { loading: listerLoading } = useSelector((state) => state.lister || {});
  const {
    qb_subscription_id,
    // qb_accounts,
    role,
    confirmed_at,
    stores_count,
    trial_start_at,
    email,
    walmart_consumer_key,
    walmart_secret_key,
    refresh_token,
    auto_ordering_auth_token,
    shopify_access_token,
    platform,
    sw_payment_flag,
    sw_store_flag,
    sw_store_sync_flag,
    store_sync_started_at
  } = user || {};
  // const validQbAccount = qb_accounts?.find((qb) => !isEmpty(qb.source_id));

  const { search } = window.location || {};
  const query = new URLSearchParams(search);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      dispatch(GetCurrentUser()).then(() => {
        if(user.id !== 1){
          dispatch(getUserCurrentSubscription()).then(() => {
            setIsUserFetched(true);
          });
        } else {
          setIsUserFetched(true);
        }
      });
    }
  }, [token]);

  const isOwner = user?.role?.id === 2; // role 2 is for Admin
  const isWarehouse = user?.role?.id === 3;
  const isOwnerWorker = user?.warehouse_manager_id == 5;
  const isAdmin = user?.role_id == 1 || user?.role_id == 3;
  const isWarehouseWorker = user?.warehouse_manager_id;
  const isWorker = user?.role_id == 5;
  const isNotSpecialUser = user?.email != "test@sales.support.com";
  const isAffiliatManager = user?.is_affiliate_manager;
  const specialUser = (role?.name === 'Warehouse' || isOwner || isAffiliatManager || isWarehouseWorker);
  const { is_public_warehouse, is_affiliate_manager } = user || {};
  const allowRemovalPages = is_public_warehouse || is_affiliate_manager;
  const isSellerPlusWarehouse = user?.role?.id === 5;

  useEffect(() => {
    const timer = setTimeout(() => {
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
      const storeSyncStartedAt = new Date(store_sync_started_at);
      const hasPendingOnboarding = !sw_payment_flag || !sw_store_flag || !sw_store_sync_flag || storeSyncStartedAt > thirtyMinutesAgo;
      if (token) {
        if(location.pathname === '/auth/sign-in'){
          if ((isOwner || isSellerPlusWarehouse) && hasPendingOnboarding){
            navigate('/auth/sign-up', { replace: true });
          }
          else {
            navigate('/inventory/sku', { replace: true })
          }
        }
        if(location.pathname === '/auth/sign-up'){
          if (isWarehouse){
            navigate('/po-warehouse', { replace: true })
          }
          else if ((isOwner || isSellerPlusWarehouse) && hasPendingOnboarding) {
            return;
          }
          else {
            navigate('/inventory/sku', { replace: true })
          }
        }
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [token, navigate, location.pathname]);

  useEffect(() => {
    if (!token || signInLoading || fetchingCurrentActiveSubscription || !fetchedSubscription || !isUserFetched) return;
    const currentPath = window.location.pathname;

    const isSubscribed = Object.keys(currentActiveSubscription).length !== 0 && (currentActiveSubscription?.status === 'active');

    // Check if user is trying to access password-related pages while logged in
    const passwordPages = ['/auth/forget-password', '/auth/password-email-verification', '/auth/password-change'];
    const isPasswordPage = passwordPages.some(page => currentPath.startsWith(page));

    if (!isEmpty(token) && isPasswordPage) {
      notification.info({
        message: 'Sign Out to visit these pages',
        top: 65,
      });
      navigate('/', { replace: true });
      return;
    }
    if (!user.is_employee_of && user.id !== 1 && (user?.role?.id === 2 || user?.role?.id === 5) && sw_payment_flag && sw_store_flag && sw_store_sync_flag && !isSubscribed && currentPath !== '/billing') {
      navigate('/billing', { replace: true });
    }
  }, [signInLoading, fetchingCurrentActiveSubscription, location, fetchedSubscription, isUserFetched]);
  useEffect(() => {
    if (fetchingCurrentUser) return;

    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    const currentPath = window?.location?.pathname;
    const reducers = ["catalogJobs", "processedCatalogs"];

    const currentGroup = [
      ...AUTH_ROUTES,
      ...ONBOARDING_ROUTES,
      ...PRIVATE_APP_ROUTES,
    ].find(route => {
      if (route.path.includes(':')) {
        const routeParts = route.path.split('/');
        const pathParts = currentPath.split('/');

        if (routeParts.length !== pathParts.length) return false;

        return routeParts.every((part, i) => {
          return part.startsWith(':') || part === pathParts[i];
        });
      }
      return route.path === currentPath;
    })?.group;

    const openOnboardingPath = currentPath.includes('auth/selling-partner');

    // Fix group for routes have dynamic values E.g pos/100. it returning undefined.

    if (currentGroup === "Auth") {
      if (!isEmpty(token) && !isEmpty(user)) {
        // console.log('confirmed_at', confirmed_at);
        // if (!confirmed_at) {
        //   return navigate("/auth/email_verification", { replace: true });
        // }

        if (specialUser) {
          dispatch({ type: "RESET_APP", reducers });
        // } else if (stores_count === 0) {
        //   navigate("/onboarding/store-add", { replace: true });
        // } else if (!qb_subscription_id && !validQbAccount) {
        //   navigate("/onboarding/select-plan", { replace: true });
        // } else {
        //   dispatch({ type: "RESET_APP", reducers });
        //   navigate("/catalog-jobs", { replace: true });
        }
      }
    // } else if (currentGroup === 'OnBoarding') {
    //   if (isEmpty(token)) {
    //     navigate("/auth/sign-in", { replace: true });
    //   } else {
    //     if (
    //       !openOnboardingPath && !(localStorage.getItem('back-to-stores')) &&
    //       (specialUser ||
    //         (stores_count > 0 && qb_subscription_id))
    //     ) {
    //       dispatch({ type: "RESET_APP", reducers });
    //       navigate("/catalog-jobs", { replace: true });
    //     }
    //   }
    } else if (currentGroup) {
      if (isSellerPlusWarehouse || isOwner) {
        if (!sw_payment_flag) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentStep: 3, maxStep: 3 }));
          navigate('/auth/sign-up', { replace: true });
          return;
        }
        if (!sw_store_flag) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentStep: 4, maxStep: 4 }));
          navigate('/auth/sign-up', { replace: true });
          return;
        }
        if (!sw_store_sync_flag){
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentStep: 4, maxStep: 4 }));
          navigate('/auth/sign-up', { replace: true });
          return;
        }
        if (sw_store_sync_flag && store_sync_started_at) {
          const startedAt = new Date(store_sync_started_at).getTime();
          const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;

          if (startedAt > thirtyMinutesAgo) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentStep: 5, maxStep: 5 }));
            navigate('/auth/sign-up', { replace: true });
            return;
          }
        }
      }
      if (!isEmpty(token) && !isEmpty(user)) {
        // if (!confirmed_at) {
        //   return navigate("/auth/email_verification", { replace: true });
        // }
        if (isMobile && !isEmpty(user) && user?.role?.id !== 2 && !MOBILE_ROUTES.some(route => currentPath.startsWith(route))) {
          return navigate('/po-mobile', { replace: true });
        }

        if (role?.name !== 'Worker' && currentPath === '/referal-users') {
          return navigate('/referal-users', { replace: true });
        } else if (role?.name === 'Worker' && currentPath === '/referal-users') {
          dispatch({ type: 'RESET_APP', reducers });
          return navigate('/catalog-jobs', { replace: true });
        }

        // if (!specialUser && stores_count === 0) {
        //   navigate("/onboarding/store-add", { replace: true });
        // } else if (!specialUser && !qb_subscription_id && !validQbAccount) {
        //   navigate("/onboarding/select-plan", { replace: true });
        // } else {
          if (currentPath === '/inventory/sku' && role?.name === 'Warehouse') return navigate('/po-warehouse', { replace: true });

          const data = { user };
          const { success, message, redirectTo, reducers } =
            handleRouteValidations(data, currentPath);
          if (!success) {
            notification.info({
              message,
              top: 65,
            });
            dispatch({ type: "RESET_APP", reducers });
            navigate(redirectTo, { replace: true });
          }
        // }
      } else {
        navigate("/auth/sign-in", { replace: true });
      }
    } else {
      if (isEmpty(token)) {
        navigate("/auth/sign-in", { replace: true });
      }
    }

    // } else {
    // notification.error({
    //   message: "Requested page doesn't exist !",
    //   top: 65,
    // })
    // navigate('/catalog-jobs', { replace: true });
    // }
  }, [
    window.location.pathname, user
  ]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  const isLoading =
    impersonatingUser ||
    fetchingCurrentUser ||
    signOutLoading ||
    warehouseLoading ||
    listerLoading ||
    fetchingCurrentActiveSubscription ||
    (token && !isUserFetched);
  const avoidRenderComponent = fetchingCurrentUser || (token && !isUserFetched);
  return (
    <div className='mainApp'>
      <ThemeProvider theme={{ ...theme }} style={{ height: '100%' }}>
        <Suspense style={{ width: '100vw', height: '100vh' }} fallback={<Spin style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} tip='Loading...'></Spin>}>
          <Spin tip='Loading...' spinning={isLoading ? true : false}>
            {avoidRenderComponent
              ? (<div style={{ width: '100vw', height: '100vh' }}></div>)
              : (
                <Routes>
                  {AUTH_ROUTES.map(({ path, component: Component }) => (
                    <Route path={path} key={path} end element={<Component />} />
                  ))}
                  {/* {ONBOARDING_ROUTES.map(({ path, component: Component }) => (
                    <Route path={path} key={path} end element={<Component />} />
                  ))} */}
                  {PRIVATE_APP_ROUTES.map(({ path, component: Component, permission }) => (
                    <Route
                      path={path}
                      key={path}
                      end
                      element={
                        <ProtectedRoute permission={permission}>
                        {isMobile ? <MobileLayout currentComponent={isEmpty(user) && user?.role?.id !== 2 ? <></> : Component} /> :
                          <Layout currentComponent={Component} />}
                        </ProtectedRoute>
                      }
                    />
                  ))}
                  {[
                    "/api/v2/affiliate_quickbooks_auth",
                    "/api/v2/distro_quickbooks_auth",
                    "/api/v2/quickbooks_auth",
                  ].map((currentURL) => (
                    <Route
                      path={currentURL}
                      key={currentURL}
                      element={
                        <RedirectToBackend
                          currentURL={currentURL}
                          navigate={() =>
                            navigate("/affiliate/account-settings", {
                              replace: true,
                            })
                          }
                        />
                      }
                    />
                  ))}
                  <Route
                    path="*"
                    element={<Navigate from="*" to="/inventory/sku" />}
                  />
                </Routes>
              )}
          </Spin>
        </Suspense>
      </ThemeProvider>
      <Toaster />
    </div>
  );
}

const RedirectToBackend = ({ currentURL, navigate }) => {
  useEffect(() => {
    redirectAPI(currentURL).then(() => navigate());
  }, []);

  const redirectAPI = (currentURL) => {
    return axios({
      method: "GET",
      url: `${process.env.API_BASE_URL}${currentURL}${window.location.search}`,
    })
      .then(({ data }) => {
        const { success, message } = data || {};
        if (success) {
          notification.success({
            message: "Authorization",
            description: message,
            top: 65,
          });
        } else {
          notification.error({
            message: "Authorization",
            description: message,
            top: 65,
          });
        }
      })
      .catch((error) => {
        const { response } = error || {};
        const { data } = response || {};
        const { message } = data || {};

        notification.error({
          message: "Authorization",
          description: message,
          top: 65,
        });
      });
  };

  return (
    <Spin
      tip="Redirecting..."
      spinning={true}
      style={{ marginTop: 100 }}
    ></Spin>
  );
};

export default App;
